import { massGrams, massHasNumber, serializeMassEntry } from './mass-entry-contract';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, EMPTY, Subject } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, switchMap, takeUntil } from 'rxjs/operators';
import { DataEntityMassFormModel } from 'src/app/Interface/DataEntity/DataEntityMassForm_Interface';
import { AlertifyService } from 'src/app/Service/Alertify.service';
import { DataEntityMassFormService } from 'src/app/Service/DataEntity/DataEntityMassForm.service';
import defaults from './data-entity-mass-form.defaults.json';
import { DataEntityMassMeasurementComponent } from './DataEntityMassMeasurement.component';
import { finiteNumberValidator, massUnitValidator, massValidationMessage, nonNegativeFiniteNumberValidator, optionalNonNegativeNumberValidator, uniqueMeasurementIdsValidator } from './data-entity-mass-form.validators';

@Component({ selector: 'app-data-entity-mass-form', templateUrl: './DataEntityMassForm.component.html', styleUrls: ['./DataEntityMassForm.component.scss'] })
export class DataEntityMassFormComponent implements OnInit, OnDestroy {
  form: FormGroup; isLoadingInitialData = false; isSubmitting = false; result: any = null;
  certificateFilePath: string = null; uncertaintyReportFilePath: string = null; isDownloading = false;
  certificateRegistrationStatus: 'registered' | 'notRegistered' = 'notRegistered';
  expandedGroupRows: boolean[] = [];
  showValidationSummary = false; saveErrorMessage = '';
  @ViewChildren(DataEntityMassMeasurementComponent) private measurementComponents: QueryList<DataEntityMassMeasurementComponent>;
  private destroy$ = new Subject<void>(); private routeId: number = null; private _entityId: number = null;
  private readonly resolvedEntityId$ = new BehaviorSubject<number>(null);
  private entityLoadingInitialized = false;
  weightClasses: string[] = [];
  referenceStandards: any[] = [];
  decisionRules: { value: number, label: string }[] = [];
  private defaultDecisionRule: number = null;
  private formVersion = 0;
  private linkedMeasurementIds = new WeakMap<AbstractControl, string>();
  // Keep malformed archived associations for review; never submit a version
  // that silently discarded them during row synchronization.
  private recoveredLinkConflict = false;
  private recoveredSource: any = null;
  private responseModelMismatch = false;
  private loadFailed = false;
  private preservedModelFields: any = {};
  readonly units = ['mg', 'g', 'kg'];
  readonly requiredMetadata = ['CustomerName', 'CertificateAddress', 'Expert', 'LabAdderss', 'IssueDateMiladi',
    'CalibrationDateMiladi', 'Manufacturer', 'ModelDevice', 'TypeDevice', 'SerialNoDevice', 'CodeNoDevice',
    'NominalRange', 'WeightClass'];
  private readonly metadataFields = ['Description', 'Expert', 'IssueDateMiladi', 'CalibrationDateMiladi',
    'RecalibrationDateMiladi', 'Manufacturer', 'ModelDevice', 'TypeDevice', 'SerialNoDevice', 'CodeNoDevice',
    'LabAdderss', 'Farsi', 'CustomerName', 'CertificateAddress', 'LetterId', 'CalibrationDetailId',
    'BehfaDeviceCodes', 'WeightClass', 'TypeClass', 'OrderNumber', 'ReceiptDate', 'NominalRange'];

  @Input() set entityId(value: number) { this._entityId = this.positiveId(value); if (this.entityLoadingInitialized) { this.resolveAndLoadEntity(); } }
  get entityId(): number { return this._entityId; }
  get certificateRegistrationMessage(): string {
    return this.certificateRegistrationStatus === 'registered'
      ? 'ثبت گواهی انجام شده است'
      : 'گواهی برای این ثبت هنوز ایجاد نشده است';
  }

  constructor(private fb: FormBuilder, private service: DataEntityMassFormService,
    private alertify: AlertifyService, private route: ActivatedRoute, private host: ElementRef) {}

  ngOnInit(): void {
    this.initializeForm(this.composeInitialData(null));
    if (this.service.configuration) { this.service.configuration().pipe(takeUntil(this.destroy$)).subscribe(config => {
      this.weightClasses = config.weightClasses || []; this.decisionRules = config.decisionRules || [];
      this.defaultDecisionRule = config.defaultDecisionRule == null ? null : config.defaultDecisionRule;
      this.nModels.controls.forEach(n => { if (n.get('DecisionRule').value == null) { n.get('DecisionRule').setValue(this.defaultDecisionRule); } });
    }, () => this.saveErrorMessage = 'دریافت کلاس‌های مجاز از سرور انجام نشد.'); }
    this.resolvedEntityId$.pipe(
      distinctUntilChanged(),
      switchMap(id => {
        if (!id) { return EMPTY; }
        this.isLoadingInitialData = true;
        return this.service.get(id).pipe(
          map(response => ({ response, id })),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 404) {
              this.referenceStandards = [];
              this.loadFailed = false; this.responseModelMismatch = false;
              this.initializeForm(this.composeInitialData(null));
              this.patchAuthoritativeMassFields(null, id);
              this.certificateRegistrationStatus = 'notRegistered';
            } else {
              this.loadFailed = true; this.form.updateValueAndValidity();
              this.saveErrorMessage = 'دریافت رکورد انجام نشد؛ برای جلوگیری از ذخیره اطلاعات رکورد قبلی، ذخیره متوقف شد.';
              this.handleError(error);
            }
            return EMPTY;
          }),
          finalize(() => this.isLoadingInitialData = false)
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(({ response, id }) => this.applyResponse(response, id, 'get'));
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.routeId = this.positiveId(params.get('id'));
      if (this.entityLoadingInitialized) { this.resolveAndLoadEntity(); }
    });
    this.entityLoadingInitialized = true;
    this.resolveAndLoadEntity();
  }
  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); this.resolvedEntityId$.complete(); }
  @HostListener('wheel', ['$event']) onWheel(event: WheelEvent): void {
    const target = event.target as HTMLInputElement; if (target && target.type === 'number' && document.activeElement === target) { target.blur(); }
  }

  get groups(): FormArray { return this.form.get('Groups') as FormArray; }
  get nModels(): FormArray { return this.form.get('NModels') as FormArray; }
  get behfaCodes(): FormArray { return this.form.get('BehfaDeviceCodes') as FormArray; }
  measurements(group: AbstractControl): FormArray { return group.get('Measurements') as FormArray; }
  asGroup(control: AbstractControl): FormGroup { return control as FormGroup; }

  addGroup(value: any = {}): void {
    const group = this.fb.group({ Unit: [value.Unit || 'g', massUnitValidator()], Measurements: this.fb.array([]) });
    this.groups.push(group);
    this.expandedGroupRows.push(this.groups.length === 1);
    (value.Measurements || []).forEach((item: any) => this.addMeasurement(group, item));
    if (!value.Measurements || !value.Measurements.length) { this.addMeasurement(group); }
  }
  removeGroup(index: number): void { this.groups.removeAt(index); this.expandedGroupRows.splice(index, 1); this.synchronizeRelatedInputs(); }
  toggleGroup(index: number): void { this.expandedGroupRows[index] = !this.expandedGroupRows[index]; }
  addMeasurement(group: FormGroup, value: any = {}): void {
    const measurement = DataEntityMassMeasurementComponent.create(this.fb, { ...value, Unit: value.Unit || group.get('Unit').value });
    (group.get('Measurements') as FormArray).push(measurement);
    this.synchronizeRelatedInputs();
    const version = this.formVersion;
    ['NominalValue','NominalDisplayUnit'].forEach(key => measurement.get(key).valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => { if (version === this.formVersion) { this.refreshLimits(); } }));
  }
  removeMeasurement(group: FormGroup, index: number): void { (group.get('Measurements') as FormArray).removeAt(index); this.synchronizeRelatedInputs(); }
  synchronizeMeasurementIds(): void { this.synchronizeRelatedInputs(); }
  addBehfaCode(value = ''): void { this.behfaCodes.push(this.fb.control(value)); }
  removeBehfaCode(index: number): void { if (this.behfaCodes.length > 1) { this.behfaCodes.removeAt(index); } }

  submitForm(): void {
    if (this.isSubmitting || this.isLoadingInitialData) { return; }
    this.saveErrorMessage = '';
    this.synchronizeRelatedInputs();
    this.form.updateValueAndValidity();
    if (this.form.invalid) {
      this.showValidationSummary = true;
      this.form.markAllAsTouched();
      this.markInvalidControlsDirty(this.form);
      this.revealInvalidMeasurements();
      this.focusFirstInvalid();
      return;
    }
    this.showValidationSummary = false;
    let request: DataEntityMassFormModel;
    try { request = this.serialize(); }
    catch (error) { this.saveErrorMessage = error.message; this.showValidationSummary = true; return; }
    this.isSubmitting = true;
    this.service.save(request).pipe(takeUntil(this.destroy$), finalize(() => this.isSubmitting = false))
      .subscribe(response => { this.applyResponse(response, this._entityId || this.routeId, 'post'); this.alertify.SuccessMessage('اطلاعات ثبت شد'); }, error => this.handleSaveError(error));
  }

  download(path: string): void {
    if (!path || this.isDownloading) { return; } this.isDownloading = true;
    this.service.downloadFile(path).pipe(takeUntil(this.destroy$), finalize(() => this.isDownloading = false))
      .subscribe(blob => { const link = document.createElement('a'); const url = URL.createObjectURL(blob); link.href = url; link.download = path.split(/[\\/]/).pop() || 'certificate'; link.click(); URL.revokeObjectURL(url); }, error => this.handleError(error));
  }

  validationMessage(control: AbstractControl): string { return massValidationMessage(control); }
  isDuplicateMeasurementId(control: AbstractControl): boolean {
    if (!this.showValidationSummary || !this.form.hasError('uniqueMeasurementIds') || !control) { return false; }
    const id = String(control.value || '').trim().toLowerCase();
    if (!id) { return false; }
    let count = 0;
    this.groups.controls.forEach(group => this.measurements(group).controls.forEach(item => {
      if (String(item.get('Id').value || '').trim().toLowerCase() === id) { count++; }
    }));
    return count > 1;
  }

  private markInvalidControlsDirty(control: AbstractControl): void {
    if (control.invalid) { control.markAsDirty({ onlySelf: true }); }
    if (control instanceof FormGroup) { Object.keys(control.controls).forEach(key => this.markInvalidControlsDirty(control.controls[key])); }
    if (control instanceof FormArray) { control.controls.forEach(child => this.markInvalidControlsDirty(child)); }
  }

  private revealInvalidMeasurements(): void {
    this.groups.controls.forEach((group, index) => {
      if (group.invalid || this.measurements(group).controls.some(item => this.isDuplicateMeasurementId(item.get('Id')))) {
        this.expandedGroupRows[index] = true;
      }
    });
    if (this.measurementComponents) {
      this.measurementComponents.forEach(component => {
        if (component.group.invalid || this.isDuplicateMeasurementId(component.group.get('Id'))) { component.expanded = true; }
      });
    }
  }

  private handleSaveError(error: HttpErrorResponse): void {
    const body: any = error && error.error;
    let fieldErrors = body && (body.errors || body.Errors);
    if (Array.isArray(fieldErrors)) { const mappedErrors: any = {}; fieldErrors.forEach(e => mappedErrors[e.path || e.Path] = e.messageFa || e.MessageFa); fieldErrors = mappedErrors; }
    let mapped = false;
    const unmappedMessages: string[] = [];
    if (fieldErrors && typeof fieldErrors === 'object' && !Array.isArray(fieldErrors)) {
      Object.keys(fieldErrors).forEach(path => {
        const control = this.controlForServerPath(path);
        const value = fieldErrors[path];
        const message = Array.isArray(value) ? value.join('، ') : String(value || 'مقدار واردشده معتبر نیست.');
        if (!control) { unmappedMessages.push(message); return; }
        control.setErrors({ ...(control.errors || {}), server: message });
        control.markAsTouched();
        control.markAsDirty();
        mapped = true;
      });
    }
    if (unmappedMessages.length) { this.saveErrorMessage = unmappedMessages.join('، '); }
    if (mapped) {
      this.showValidationSummary = true;
      this.revealInvalidMeasurements();
      this.focusFirstInvalid();
    } else if (!unmappedMessages.length) {
      this.saveErrorMessage = 'ثبت انجام نشد. اطلاعات فرم و تنظیمات روش را بررسی کنید.';
    }
  }

  private controlForServerPath(path: string): AbstractControl {
    path = path.replace(/^\$\.?/, '').replace(/^(massModel|model)\./i, '');
    path = path.replace(/\.AsFoundCycles(?=\[|$)/gi, '.Cycles');
    const flat = /^Groups\[0\]\.Measurements\[(\d+)\](.*)$/i.exec(path);
    if (flat && this.groups.length > 1) {
      let remaining = Number(flat[1]);
      for (let gi = 0; gi < this.groups.length; gi++) {
        const length = this.measurements(this.groups.at(gi)).length;
        if (remaining < length) { path = 'Groups[' + gi + '].Measurements[' + remaining + ']' + flat[2]; break; }
        remaining -= length;
      }
    }
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
    if (/^(massModel|model)$/i.test(parts[0])) { parts.shift(); }
    let control: AbstractControl = this.form;
    for (const part of parts) {
      if (control instanceof FormGroup) {
        const key = Object.keys(control.controls).find(name => name.toLowerCase() === part.toLowerCase());
        control = key ? control.controls[key] : null;
      } else if (control instanceof FormArray && /^\d+$/.test(part)) {
        control = control.at(Number(part));
      } else { return null; }
      if (!control) { return null; }
    }
    return control === this.form ? null : control;
  }

  private resolveAndLoadEntity(): void { this.resolvedEntityId$.next(this._entityId || this.routeId); }

  private applyResponse(response: any, calibrationDetailId: number, source: 'get' | 'post'): void {
    if (source === 'get') { this.loadFailed = false; }
    const body = this.unwrapResponse(response);
    const standards = this.apiValue(body, 'referenceStandards', 'ReferenceStandards');
    if (Array.isArray(standards)) { this.referenceStandards = standards; } else if (source === 'get') { this.referenceStandards = []; }
    const massModel = this.apiValue(body, 'massModel', 'MassModel');
    const fallbackModel = this.apiValue(body, 'samplerModel', 'SamplerModel');
    // A historical response alias may contain a Mass model or metadata. An
    // actual SamplerModel has different quantities and must not become Mass.
    if (!massModel && fallbackModel && Object.keys(fallbackModel).some(key => ['m0','m2','pb','td20','dmtdb'].includes(key.toLowerCase()))) {
      this.responseModelMismatch = true;
      this.saveErrorMessage = 'پاسخ سرویس مربوط به کالیبراسیون حجم است؛ داده‌های آن وارد فرم جرم نشد. ذخیره متوقف شد.';
      this.showValidationSummary = true; this.form.updateValueAndValidity(); return;
    }
    this.responseModelMismatch = false;
    const model = this.normalizeKeys(massModel || fallbackModel || null);
    const certificateFile = this.find(model, 'Certificatefile', 'CertificateFile') ||
      this.apiValue(body, 'certificatefile', 'Certificatefile', 'certificateFile', 'CertificateFile');
    const registered = this.hasText(certificateFile);
    if (source === 'get') {
      this.showValidationSummary = false;
      this.saveErrorMessage = '';
      this.initializeForm(this.composeInitialData(model));
      this.patchAuthoritativeMassFields(model, calibrationDetailId);
    } else if (model) {
      this.initializeForm(this.composeInitialData(model));
      this.patchAuthoritativeMassFields(model, calibrationDetailId);
    }
    if (source === 'get' || model || certificateFile !== undefined) {
      this.certificateRegistrationStatus = registered ? 'registered' : 'notRegistered';
      this.certificateFilePath = registered ? certificateFile : null;
    }
    this.result = this.apiValue(body, 'certificate', 'Certificate', 'result', 'Result') || null;
    const reportFile = this.find(model, 'UncertaintyReportFile') ||
      this.apiValue(body, 'uncertaintyReportFile', 'UncertaintyReportFile');
    if (source === 'get' || reportFile !== undefined) { this.uncertaintyReportFilePath = reportFile || null; }
    const errors = this.apiValue(body, 'errors', 'Errors');
    if (source === 'get' && Array.isArray(errors) && errors.length) {
      this.handleSaveError({ error: { errors } } as HttpErrorResponse);
    }
  }

  private patchAuthoritativeMassFields(model: any, calibrationDetailId: number): void {
    const source = model || {};
    const fields = ['LetterId', 'CalibrationDateMiladi', 'IssueDateMiladi', 'CodeNoDevice',
      'SerialNoDevice', 'CustomerName', 'CertificateAddress', 'RecalibrationDateMiladi',
      'Description', 'Manufacturer', 'ModelDevice', 'TypeDevice'];
    fields.forEach(field => {
      const control = this.form.get(field);
      if (control && source[field] !== undefined) {
        control.patchValue(this.clone(source[field]), { emitEvent: false });
      }
    });
    const idControl = this.form.get('CalibrationDetailId');
    const modelId = this.positiveId(source.CalibrationDetailId);
    if (idControl && (modelId || calibrationDetailId)) {
      idControl.patchValue(modelId || calibrationDetailId, { emitEvent: false });
    }
  }

  private unwrapResponse(response: any): any {
    let current = response;
    for (let depth = 0; depth < 5 && current && typeof current === 'object'; depth++) {
      if (this.apiValue(current, 'massModel', 'MassModel', 'samplerModel', 'SamplerModel') !== undefined) { return current; }
      const nested = ['data', 'body', 'result', 'value']
        .map(key => this.apiValue(current, key)).find(value => value && typeof value === 'object');
      if (!nested || typeof nested !== 'object') { break; }
      current = nested;
    }
    return current || {};
  }

  private apiValue(source: any, ...keys: string[]): any {
    if (!source || typeof source !== 'object') { return undefined; }
    for (const key of keys) { if (Object.prototype.hasOwnProperty.call(source, key)) { return source[key]; } }
    return undefined;
  }

  private initializeForm(data: any): void {
    this.formVersion++;
    const structureDefaults = this.massStructureDefaults();
    const merged = { ...this.emptyMetadata(), ...(data || {}),
      Groups: data && Array.isArray(data.Groups) ? data.Groups : structureDefaults.Groups,
      NModels: data && Array.isArray(data.NModels) ? data.NModels : structureDefaults.NModels,
      WeightInput: { ...structureDefaults.WeightInput, ...((data && data.WeightInput) || {}) } };
    this.recoveredSource = this.clone(data || {});
    this.preservedModelFields = {};
    ['UncertainyFloat','Value','From','To','Division','Unit'].forEach(key => {
      if (data && data[key] !== undefined) { this.preservedModelFields[key] = this.clone(data[key]); }
    });
    const ids: string[] = [];
    (merged.Groups || []).forEach(g => (g.Measurements || []).forEach(m => ids.push(String(m.Id || '').toLowerCase())));
    this.recoveredLinkConflict = [merged.NModels || [], merged.WeightInput.Items || []].some(rows => {
      const seen: string[] = [];
      return rows.some(row => {
        const id = String(row && row.MeasurementId || '').toLowerCase();
        const invalid = !id || !ids.includes(id) || seen.includes(id); seen.push(id); return invalid;
      });
    });
    const controls: any = {};
    controls.ContractVersion = [merged.ContractVersion == null ? 2 : merged.ContractVersion,
      c => [1,2].includes(c.value) ? null : { contractVersion: true }];
    ['WeightClass','Description','Expert','IssueDateMiladi','CalibrationDateMiladi','RecalibrationDateMiladi','Manufacturer','ModelDevice','TypeDevice','SerialNoDevice','CodeNoDevice','LabAdderss','CustomerName','CertificateAddress','TypeClass','OrderNumber','ReceiptDate','NominalRange']
      .forEach(key => controls[key] = [merged[key] == null ? '' : merged[key], this.requiredMetadata.indexOf(key) >= 0 ? Validators.required : []]);
    const positiveIdValidator = (c: AbstractControl) => massHasNumber(c.value) && Number.isSafeInteger(Number(c.value)) && Number(c.value) > 0 ? null : { positiveId: true };
    controls.Farsi = [merged.Farsi !== false]; controls.LetterId = [merged.LetterId || 0, positiveIdValidator];
    controls.CalibrationDetailId = [merged.CalibrationDetailId || 0, positiveIdValidator];
    controls.BehfaDeviceCodes = this.fb.array((merged.BehfaDeviceCodes || []).map((x: string) => this.fb.control(x)));
    controls.Groups = this.fb.array([]); controls.NModels = this.fb.array([]); controls.WeightInput = this.createWeightInput(merged.WeightInput);
    this.form = this.fb.group(controls, { validators: [uniqueMeasurementIdsValidator(),
      () => this.recoveredLinkConflict ? { relatedInputConflict: true } : null,
      () => this.responseModelMismatch ? { responseModelMismatch: true } : null,
      () => this.loadFailed ? { loadFailed: true } : null] });
    this.expandedGroupRows = [];
    (merged.Groups || []).forEach((group: any) => this.addGroup(group)); if (!this.groups.length) { this.addGroup(); }
    this.synchronizeRelatedInputs(merged.NModels || [], merged.WeightInput && merged.WeightInput.Items || []);
    this.form.get('WeightClass').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.refreshLimits());
    this.refreshLimits(false);
  }

  private synchronizeRelatedInputs(existingN: any[] = this.nModels.value, existingItems: any[] = (this.form.get('WeightInput.Items') as FormArray).value): void {
    if (!this.form) { return; } const rows: any[] = [];
    this.groups.controls.forEach(group => this.measurements(group).controls.forEach(measurement => rows.push(measurement)));
    const nArray = this.fb.array([]) as FormArray; const itemArray = this.fb.array([]) as FormArray;
    rows.forEach((measurement, index) => {
      const id = String(measurement.get('Id').value || ''); const unit = measurement.get('Unit').value || 'g';
      // A changed Id belongs to the same form row. Keep its linked reference
      // and acceptance data rather than replacing them with blank inputs.
      const previousId = this.linkedMeasurementIds.get(measurement);
      const linkedId = previousId === undefined ? id : previousId;
      const matches = (x: any) => String(x.MeasurementId).toLowerCase() === linkedId.toLowerCase();
      const n = existingN.find(matches) || {};
      let nominal = n.Value;
      if (nominal == null && massHasNumber(measurement.get('NominalValue').value)) {
        nominal = String(massGrams(measurement.get('NominalValue').value, measurement.get('NominalDisplayUnit').value));
      }
      nArray.push(this.createNModel({ ...n, MeasurementId: id, Value: nominal }));
      const item = existingItems.find(matches) || {};
      itemArray.push(this.createReferenceItem(id, item, unit));
      this.linkedMeasurementIds.set(measurement, id);
    });
    this.form.setControl('NModels', nArray); (this.form.get('WeightInput') as FormGroup).setControl('Items', itemArray);
  }

  private createReferenceItem(id: string, item: any, unit: string): FormGroup {
    const group = this.fb.group({ MeasurementId: [id, Validators.required], Msn: this.createExpanded(item.Msn, unit, false), MsnUncertainty: item.MsnUncertainty ? this.createExpanded(item.MsnUncertainty, unit) : this.fb.control(null), DmCal: this.createExpanded(item.DmCal, unit), DmDrift: this.createRectangular(item.DmDrift, unit) }, {
      validators: (control: AbstractControl) => {
        const value = control.value;
        return [value.Msn, value.MsnUncertainty, value.DmCal].some(u => u && (Number(u.ExpandedUncertainty) > 0 || Number(u.StandardUncertainty) > 0)) ? null : { referenceUncertainty: true };
      }
    });
    ['Msn', 'MsnUncertainty', 'DmCal', 'DmDrift'].forEach(key => {
      const control = group.get(key + '.Unit'); if (control) { control.setValidators(massUnitValidator()); control.updateValueAndValidity(); }
    });
    return group;
  }
  private createNModel(v: any): FormGroup {
    // LimitValue=3 and Limit=0 are the existing NModel initializers; MPE and
    // tolerance have no invented fallback and are obtained from MassLimits.
    return this.fb.group({ MeasurementId:[v.MeasurementId || '', Validators.required], Value:[v.Value == null ? '' : v.Value, Validators.required],
      Tolerance:[v.Tolerance == null ? null : v.Tolerance, nonNegativeFiniteNumberValidator()], Limit:[v.Limit === undefined ? 0 : v.Limit, finiteNumberValidator()],
      LowerLimit:[v.LowerLimit == null ? null : v.LowerLimit, finiteNumberValidator()], UpperLimit:[v.UpperLimit == null ? null : v.UpperLimit, finiteNumberValidator()],
      LimitValue:[v.LimitValue === undefined ? 3 : v.LimitValue, [finiteNumberValidator(), Validators.min(Number.MIN_VALUE)]], DecisionRule:[v.DecisionRule == null ? this.defaultDecisionRule : v.DecisionRule, Validators.required], Distribution:this.createDistribution(v.Distribution, 'Normal') });
  }
  private createWeightInput(v: any): FormGroup { const controls: any = { Items: this.fb.array([]) }; ['Upa','Pr','P0','Pal','Upr','Pt','PaInd','TaInd','QaInd'].forEach(k => controls[k] = this.createConstant(v[k])); ['Upt','Dsensitivity','Dresolution','Deccentricity','PaRes','TaRes','QRes'].forEach(k => controls[k] = this.createRectangular(v[k])); ['PaCal','TaCal','QCal'].forEach(k => controls[k] = this.createExpanded(v[k])); return this.fb.group(controls); }
  private createConstant(value: any = {}): FormGroup {
    const v = value || {};
    return this.fb.group({ Name:[v.Name == null ? null : v.Name], Value:[v.Value == null ? null : v.Value, finiteNumberValidator()], Unit:[v.Unit === undefined ? '' : v.Unit, Validators.required] });
  }
  private createExpanded(value: any = {}, unit = '', requireUncertainty = true): FormGroup {
    const v = value || {};
    return this.fb.group({ Name:[v.Name == null ? null : v.Name], Value:[v.Value == null ? null : v.Value, finiteNumberValidator()], Unit:[v.Unit === undefined ? unit : v.Unit, Validators.required],
      DoF:[v.DoF == null ? null : v.DoF], StandardUncertainty:[v.StandardUncertainty == null ? null : v.StandardUncertainty, optionalNonNegativeNumberValidator()],
      ExpandedUncertainty:[v.ExpandedUncertainty == null ? null : v.ExpandedUncertainty, optionalNonNegativeNumberValidator()],
      CoverageFactor:[v.CoverageFactor == null ? null : v.CoverageFactor, optionalNonNegativeNumberValidator()], Distribution:this.createDistribution(v.Distribution, 'Normal') }, { validators: control => {
        const u = control.value, errors: any = {};
        if (requireUncertainty && !massHasNumber(u.StandardUncertainty) && !massHasNumber(u.ExpandedUncertainty)) { errors.uncertaintyRequired = true; }
        if ((Number(u.ExpandedUncertainty) > 0 || Number(u.StandardUncertainty) > 0) && !(Number(u.CoverageFactor) > 0)) { errors.coverageRequired = true; }
        return Object.keys(errors).length ? errors : null;
      } });
  }
  private createRectangular(value: any = {}, unit = ''): FormGroup {
    const v = value || {};
    return this.fb.group({ Name:[v.Name == null ? null : v.Name], Value:[v.Value == null ? null : v.Value, finiteNumberValidator()], Unit:[v.Unit === undefined ? unit : v.Unit, Validators.required], DoF:[v.DoF == null ? null : v.DoF],
      StandardUncertainty:[v.StandardUncertainty == null ? null : v.StandardUncertainty, optionalNonNegativeNumberValidator()], RelativeErrorOfUncertainty:[v.RelativeErrorOfUncertainty == null ? null : v.RelativeErrorOfUncertainty, optionalNonNegativeNumberValidator()],
      HalfWidthOfLimit:[v.HalfWidthOfLimit == null ? null : v.HalfWidthOfLimit, optionalNonNegativeNumberValidator()], Distribution:this.createDistribution(v.Distribution, 'Rectangular') },
      { validators: c => !massHasNumber(c.value.StandardUncertainty) && !massHasNumber(c.value.HalfWidthOfLimit) ? { rectangularRequired: true } : null });
  }
  private createDistribution(value: any = {}, type: string): FormGroup {
    const v = value === null ? { $type: null, CoverageFactor: null, ShapeFactor: null } : value;
    return this.fb.group({ '$type':[v.$type === undefined ? type : v.$type, Validators.required],
      CoverageFactor:[v.CoverageFactor === undefined ? (type === 'Normal' ? 2 : 1.65) : v.CoverageFactor, nonNegativeFiniteNumberValidator()],
      ShapeFactor:[v.ShapeFactor === undefined ? 1 : v.ShapeFactor, nonNegativeFiniteNumberValidator()] });
  }
  private composeInitialData(massModel: any): any {
    const structureDefaults = this.massStructureDefaults();
    const normalizedModel = massModel ? this.normalizeKeys(massModel) : this.clone(defaults);
    const metadata = this.emptyMetadata();

    this.patchMetadata(metadata, normalizedModel);

    return {
      ...metadata,
      ...['UncertainyFloat','Value','From','To','Division','Unit'].reduce((fields, key) => {
        if (normalizedModel && normalizedModel[key] !== undefined) { fields[key] = normalizedModel[key]; } return fields;
      }, {}),
      ContractVersion: normalizedModel && normalizedModel.ContractVersion != null ? normalizedModel.ContractVersion : 2,
      Groups: normalizedModel && Array.isArray(normalizedModel.Groups)
        ? normalizedModel.Groups : structureDefaults.Groups,
      NModels: normalizedModel && Array.isArray(normalizedModel.NModels)
        ? normalizedModel.NModels : structureDefaults.NModels,
      WeightInput: normalizedModel && normalizedModel.WeightInput
        ? normalizedModel.WeightInput : structureDefaults.WeightInput
    };
  }
  private massStructureDefaults(): any {
    const source: any = this.clone(defaults);
    return {
      Groups: Array.isArray(source.Groups) ? source.Groups : [],
      NModels: Array.isArray(source.NModels) ? source.NModels : [],
      WeightInput: source.WeightInput || { Items: [] }
    };
  }
  private emptyMetadata(): any {
    return {
      Description: '', Expert: '', IssueDateMiladi: '', CalibrationDateMiladi: '',
      RecalibrationDateMiladi: '', Manufacturer: '', ModelDevice: '', TypeDevice: '',
      SerialNoDevice: '', CodeNoDevice: '', LabAdderss: '', Farsi: true,
      CustomerName: '', CertificateAddress: '', LetterId: 0, CalibrationDetailId: 0,
      WeightClass: '', BehfaDeviceCodes: [], TypeClass: '', OrderNumber: '', ReceiptDate: '', NominalRange: ''
    };
  }
  private patchMetadata(target: any, source: any): void {
    if (!source || typeof source !== 'object') { return; }
    this.metadataFields.forEach(field => {
      if (source[field] !== undefined && source[field] !== null) { target[field] = this.clone(source[field]); }
    });
    if (!target.CertificateAddress && source.CustomerAddress) {
      target.CertificateAddress = source.CustomerAddress;
    }
  }
  private serialize(): DataEntityMassFormModel { return serializeMassEntry({ ...this.preservedModelFields, ...this.form.getRawValue() }) as DataEntityMassFormModel; }
  private positiveId(value: any): number { const n = Number(value); return Number.isSafeInteger(n) && n > 0 ? n : null; }
  private clone<T>(value: T): T { return JSON.parse(JSON.stringify(value)); }
  private find(source: any, ...keys: string[]): any { if (!source) { return null; } for (const key of keys) { if (source[key]) { return source[key]; } } return null; }
  private hasText(value: any): boolean { return typeof value === 'string' && value.trim().length > 0; }
  private normalizeKeys(value: any): any { if (!value || typeof value !== 'object') { return value; } if (Array.isArray(value)) { return value.map(x => this.normalizeKeys(x)); } const result: any = {}; Object.keys(value).forEach(k => result[k.charAt(0).toUpperCase() + k.slice(1)] = this.normalizeKeys(value[k])); return result; }
  private handleError(error: HttpErrorResponse): void { const details: any = error && error.error; const message = details && (details.detail || details.title) || 'خطا در ارتباط با سرویس Mass'; this.alertify.ErrorMessage(message); }
  resetForm(): void {
    const data = this.composeInitialData(null);
    this.metadataFields.filter(key => key !== 'WeightClass' && key !== 'NominalRange').forEach(key => {
      const control = this.form.get(key); if (control) { data[key] = this.clone(control.value); }
    });
    this.showValidationSummary = false; this.saveErrorMessage = '';
    this.initializeForm(data);
  }
  get validationErrors(): { path: string, message: string }[] {
    const errors: { path: string, message: string }[] = [];
    const walk = (c: AbstractControl, path: string) => {
      if (c instanceof FormGroup) { if (c.errors) { Object.keys(c.errors).filter(key => key !== 'massCycles' || !c.hasError('asFoundRequired')).forEach(key => {
        const field = { nominalMagnitude: 'NominalValue', repeatabilityRequired: 'TheoreticalSPool', asFoundRequired: 'Cycles', stageRequired: 'AsLeftCycles', referenceUncertainty: 'Msn.ExpandedUncertainty', coverageRequired: 'CoverageFactor' }[key];
        errors.push({ path: field && path ? path + '.' + field : path, message: massValidationMessage(c, key) });
      }); } Object.keys(c.controls).forEach(k => walk(c.controls[k], path ? path + '.' + k : k)); }
      else if (c instanceof FormArray) { if (c.errors) { errors.push({ path, message: massValidationMessage(c) }); } c.controls.forEach((v,i) => walk(v, path + '[' + i + ']')); }
      else if (c.invalid && (c.touched || c.dirty)) { errors.push({ path, message: massValidationMessage(c) }); }
    };
    if (this.form) { walk(this.form, ''); } return errors;
  }
  focusError(path: string): void {
    const c = this.controlForServerPath(path); if (c) { c.markAsTouched(); }
    this.revealInvalidMeasurements();
    setTimeout(() => {
      const match = /^(Groups\[\d+\]\.Measurements\[\d+\])(?:\.(.+))?$/.exec(path);
      let container = match ? this.host.nativeElement.querySelector('[data-mass-path="' + match[1] + '"]') : this.host.nativeElement;
      let name = match ? (match[2] || 'NominalValue').split('.').pop() : path;
      name = name.replace(/^AsFoundCycles$/, 'Cycles');
      const reference = /^WeightInput\.Items\[(\d+)\]\.([^.]+)\.([^.]+)$/.exec(path);
      const shared = /^WeightInput\.([^.]+)\.([^.]+)$/.exec(path);
      if (reference) { container = this.host.nativeElement.querySelector('[data-mass-reference-index="' + reference[1] + '"] [data-mass-parameter="' + reference[2] + '"]'); name = reference[3]; }
      else if (shared) { container = this.host.nativeElement.querySelector('[data-mass-key="' + shared[1] + '"], [data-mass-parameter="' + shared[1] + '"]'); name = shared[2]; }
      const target = container && container.querySelector('[formcontrolname="' + name + '"], [data-mass-field="' + name + '"]');
      if (target) { let parent = target.parentElement; while (parent) { if (parent.tagName === 'DETAILS') { (parent as HTMLDetailsElement).open = true; } parent = parent.parentElement; } target.scrollIntoView({ behavior: 'smooth', block: 'center' }); target.focus({ preventScroll: true }); } else { this.focusFirstInvalid(); }
    });
  }
  refreshLimits(updateRange = true): void {
    const version = this.formVersion;
    const nominals: string[] = [];
    this.groups.controls.forEach(g => this.measurements(g).controls.forEach(m => nominals.push(m.get('NominalValue').value == null ? '' : m.get('NominalValue').value + ' ' + m.get('NominalDisplayUnit').value)));
    if (updateRange) { this.form.get('NominalRange').setValue(nominals.join(', '), { emitEvent: false }); }
    if (!this.service.limits || !this.form.get('WeightClass').value) { return; }
    this.groups.controls.forEach(g => this.measurements(g).controls.forEach(m => {
      const nominalControl = m.get('NominalValue');
      if (nominalControl.hasError('mpe')) { const errors = { ...nominalControl.errors }; delete errors.mpe; nominalControl.setErrors(Object.keys(errors).length ? errors : null); }
      const nominal = nominalControl.value, unit = m.get('NominalDisplayUnit').value, cls = this.form.get('WeightClass').value;
      if (!massHasNumber(nominal) || Number(nominal) <= 0 || !this.units.includes(unit)) { return; }
      nominalControl.setErrors({ ...(nominalControl.errors || {}), limitsPending: true });
      this.service.limits(nominal, unit, cls).pipe(takeUntil(this.destroy$)).subscribe(limits => {
        if (version !== this.formVersion || nominal !== m.get('NominalValue').value || unit !== m.get('NominalDisplayUnit').value || cls !== this.form.get('WeightClass').value) { return; }
        const n = this.nModels.controls.find(n => n.get('MeasurementId').value === m.get('Id').value);
        if (n) { n.patchValue({ Value: String(limits.nominalGrams), Tolerance: limits.mpe * 2, LowerLimit: limits.lowerSpecificationLimit, UpperLimit: limits.upperSpecificationLimit }, { emitEvent: false }); }
        const errors = { ...(nominalControl.errors || {}) }; delete errors.limitsPending;
        nominalControl.setErrors(Object.keys(errors).length ? errors : null);
      }, () => {
        if (version === this.formVersion && nominal === m.get('NominalValue').value && unit === m.get('NominalDisplayUnit').value && cls === this.form.get('WeightClass').value) {
          const errors: { [key: string]: any } = { ...(nominalControl.errors || {}), mpe: true }; delete errors.limitsPending;
          nominalControl.setErrors(errors);
        }
      });
    }));
  }
  private focusFirstInvalid(): void {
    setTimeout(() => {
      const input: HTMLElement = this.host.nativeElement.querySelector(
        'input.ng-invalid.ng-touched, select.ng-invalid.ng-touched, textarea.ng-invalid.ng-touched, .mass-field-error');
      if (!input) { return; }
      let parent = input.parentElement; while (parent) { if (parent.tagName === 'DETAILS') { (parent as HTMLDetailsElement).open = true; } parent = parent.parentElement; }
      if (input.scrollIntoView) { input.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
      input.focus({ preventScroll: true });
    });
  }
}
